import { isEqual } from 'lodash'
import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { Crumbs } from '#/components/PageHeader'
import { PageHeaderContextData, PageHeaderOptions } from '#/contexts/PageHeader/types'

const initialState: PageHeaderOptions = {}

const PageHeaderContext = createContext<PageHeaderContextData>({
  pageHeader: initialState,
  resetPageHeader: () => undefined,
  setCrumbMap: () => undefined,
  setCrumbs: () => undefined,
  setDescription: () => undefined,
  setHidden: () => undefined,
  setPageHeader: () => undefined,
  setSubtitle: () => undefined,
  setTitle: () => undefined,
})

export function PageHeaderProvider({ children }: PropsWithChildren) {
  const [pageHeader, setPageHeaderState] = useState<PageHeaderOptions>(initialState)

  /**
   * Merge parcial: atualiza somente os campos informados, preservando os demais.
   * Se nada mudou, mantém a mesma referência de estado para evitar re-renders.
   */
  const setPageHeader = useCallback((options: PageHeaderOptions) => {
    setPageHeaderState(currentOptions => {
      const nextOptions = { ...currentOptions, ...options }
      return isEqual(currentOptions, nextOptions) ? currentOptions : nextOptions
    })
  }, [])

  const resetPageHeader = useCallback(() => {
    setPageHeaderState(initialState)
  }, [])

  const setTitle = useCallback((title: ReactNode) => setPageHeader({ title }), [setPageHeader])

  const setSubtitle = useCallback(
    (subtitle: ReactNode) => setPageHeader({ subtitle }),
    [setPageHeader],
  )

  const setCrumbs = useCallback(
    (crumbs: Crumbs[] | null) => setPageHeader({ crumbs }),
    [setPageHeader],
  )

  const setDescription = useCallback(
    (description: ReactNode) => setPageHeader({ description }),
    [setPageHeader],
  )

  const setCrumbMap = useCallback((crumbMap: PageHeaderOptions['crumbMap']) => {
    setPageHeaderState(currentOptions => {
      const nextOptions = {
        ...currentOptions,
        crumbMap: { ...currentOptions.crumbMap, ...crumbMap },
      }
      return isEqual(currentOptions, nextOptions) ? currentOptions : nextOptions
    })
  }, [])

  const setHidden = useCallback((hidden: boolean) => setPageHeader({ hidden }), [setPageHeader])

  const contextValue = useMemo(
    () => ({
      pageHeader,
      resetPageHeader,
      setCrumbMap,
      setCrumbs,
      setDescription,
      setHidden,
      setPageHeader,
      setSubtitle,
      setTitle,
    }),
    [
      pageHeader,
      resetPageHeader,
      setCrumbs,
      setDescription,
      setHidden,
      setPageHeader,
      setSubtitle,
      setTitle,
      setCrumbMap,
    ],
  )

  return <PageHeaderContext.Provider value={contextValue}>{children}</PageHeaderContext.Provider>
}

/**
 * Acesso ao cabeçalho global de página.
 *
 * Uso declarativo — passa as opções direto no hook, sem useEffect na tela;
 * elas são (re)aplicadas a cada render e limpas automaticamente na troca de rota:
 *   usePageHeader({ title: pageTitle, crumbs: [...] })
 *
 * Uso imperativo — os setters retornados servem para atualizar campos depois,
 * a partir de callbacks (resposta de request, handlers) ou useEffect —
 * nunca no corpo do render:
 *   const { setSubtitle } = usePageHeader()
 */
export function usePageHeader(options?: PageHeaderOptions) {
  const pageHeaderContext = useContext(PageHeaderContext)
  const { setPageHeader } = pageHeaderContext

  // Sem array de deps de propósito: o bailout por isEqual no setPageHeader
  // transforma renders sem mudança em no-op, sem re-render extra
  useEffect(() => {
    if (options) {
      setPageHeader(options)
    }
  })

  return pageHeaderContext
}
